import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button, Toast } from 'src/components';
import { newExperienceBlogPosts } from 'src/apps/Classic/MigrationBanner/migration.const';
import { intlGetMessagesLocale } from 'src/utils/intl';
import { MeApi, ScenariosBetaApi } from 'src/api';
import useSafePush from 'src/hooks/useSafePush';
import { useUserContext } from 'src/context/user';
import { addToLocalStorageArrayEntry } from 'src/utils/webStorageArrayUtils';
import {
  CLASSIC_MIGRATION_ONBOARDING_STORAGE_KEY,
  WELCOME_TO_MEMRISE_STORAGE_KEY,
} from 'src/constants/storageKeys.const';
import { setLocalStorageEntry } from 'src/utils/webStorageUtils';
import { SyncLoader } from 'src/apps/Tools/Sync/SyncLoader';
import { ZENDESK } from 'src/constants/links.const';
import * as S from './styles';

const msgs = defineMessages({
  header: { id: 'migration_classic_milestone2_launch_header' },
  headerIntro: { id: 'migration_classic_milestone2_launch_header_intro' },
  descriptionTitle: { id: 'migration_classic_milestone2_body_launch_title' },
  description1: { id: 'migration_classic_milestone2_body_launch_description_1' },
  description2: { id: 'migration_classic_milestone2_body_launch_description_2_web' },
  description3Hybrid: { id: 'migration_hybrid_milestone2_body_launch_description_3_web' },
  launchNewExperience: { id: 'migration_learn_more_launch_cta' },
  continue: { id: 'Continue' },
  loaderHeader: { id: 'ea_switch_sync_title' },
  loaderDescription: { id: 'migration_classic_onboarding_description' },
  errorDescription: { id: 'error_page_client_error_title' },
  reportBug: { id: 'learning_session_error_button_report_bug' },
});

const SYNC_MIN_DURATION_MS = 4000;

export const NewExperienceBanner: FC<{ isHybrid?: boolean }> = ({ isHybrid }) => {
  const intl = useIntl();
  const { safeReload } = useSafePush();
  const { user } = useUserContext();
  const [migrationStarted, setMigrationStarted] = useState(false);
  const [migrationFinished, setMigrationFinished] = useState(false);
  const [timeoutFinished, setTimeoutFinished] = useState(false);
  const [isError, setIsError] = useState(false);

  const createLinkTag =
    (url: string) =>
    // eslint-disable-next-line react/display-name
    (...chunks: ReactNode[]) => (
      <S.LinkTag key="LinkTag" href={url} target="_blank">
        {chunks}
      </S.LinkTag>
    );

  const handleMigration = async () => {
    await MeApi.convertClassicProgress();
    await ScenariosBetaApi.forceJoinScenariosBeta();
  };

  const onLaunchNewExperience = useCallback(() => {
    setMigrationStarted(true);
    setIsError(false);
    setTimeoutFinished(false);

    handleMigration()
      .then(() => {
        setMigrationFinished(true);
      })
      .catch(() => {
        setIsError(true);
        setMigrationStarted(false);
      });

    setTimeout(() => {
      setTimeoutFinished(true);
    }, SYNC_MIN_DURATION_MS);
  }, [setMigrationStarted, setMigrationFinished, setTimeoutFinished, setIsError]);

  useEffect(() => {
    if (migrationFinished && timeoutFinished) {
      addToLocalStorageArrayEntry(WELCOME_TO_MEMRISE_STORAGE_KEY, String(user?.id));
      setLocalStorageEntry(CLASSIC_MIGRATION_ONBOARDING_STORAGE_KEY, 'true');
      safeReload();
    }
  }, [migrationFinished, timeoutFinished, safeReload, user?.id]);

  if (migrationStarted) {
    return (
      <SyncLoader
        fullSize
        stopProcess={false}
        isOpen
        setOpenModal={() => {}}
        header={intl.formatMessage(msgs.loaderHeader)}
        description={intl.formatMessage(msgs.loaderDescription)}
      />
    );
  }

  return (
    <S.ModalWrapper isOpen hideCloseButton fullSize>
      <S.NewExperienceModalContent $isHybrid={isHybrid}>
        <S.Header>
          <S.Title>{intl.formatMessage(msgs.header)}</S.Title>
          <S.Subtitle>{intl.formatMessage(msgs.headerIntro)}</S.Subtitle>
        </S.Header>
        <S.Content>
          {isError && (
            <S.ErrorWrapper>
              <Toast
                status="error"
                linkHref={ZENDESK}
                linkText={intl.formatMessage(msgs.reportBug)}
                linkNewTab
              >
                {intl.formatMessage(msgs.errorDescription)}
              </Toast>
            </S.ErrorWrapper>
          )}

          <S.DescriptionHybrid>{intl.formatMessage(msgs.descriptionTitle)}</S.DescriptionHybrid>
          <S.DescriptionList>
            <S.DescriptionListItem>{intl.formatMessage(msgs.description1)}</S.DescriptionListItem>
            <S.DescriptionListItem>
              {intl.formatMessage(msgs.description2, {
                a: createLinkTag(
                  newExperienceBlogPosts[intlGetMessagesLocale(intl.locale)] ||
                    newExperienceBlogPosts.en,
                ),
              })}
            </S.DescriptionListItem>
            {isHybrid && (
              <S.DescriptionListItem>
                {intl.formatMessage(msgs.description3Hybrid, {
                  a: createLinkTag('https://community-courses.memrise.com'),
                })}
              </S.DescriptionListItem>
            )}
          </S.DescriptionList>
        </S.Content>
      </S.NewExperienceModalContent>
      <S.ButtonWrapperFooter>
        <Button buttonType="primary" buttonSize="medium" onClick={onLaunchNewExperience}>
          {intl.formatMessage(msgs.launchNewExperience)}
        </Button>
      </S.ButtonWrapperFooter>
    </S.ModalWrapper>
  );
};
